<template>
  <BaseModal
    :open="open"
    :aria-label="$t('language_modal.title')"
    card-class="ls-card"
    @update:open="onOpenUpdate"
  >
    <div class="ls-head">
      <h3>{{ $t('language_modal.title') }}</h3>
      <p class="ls-sub">{{ $t('language_modal.subtitle') }}</p>
    </div>
    <p class="ls-hint">{{ $t('language_modal.select_hint') }}</p>
    <div class="ls-body">
      <button
        v-for="loc in locales"
        :key="loc.code"
        class="ls-row"
        :class="{ 'is-active': loc.code === current }"
        @click="pick(loc.code)"
      >
        <span class="ls-flag" :class="`flag-${loc.flag}`" aria-hidden="true">
          <img
            v-if="!flagFailed[loc.code]"
            :src="flagUrl(loc.flag)"
            :alt="loc.label"
            loading="lazy"
            decoding="async"
            @error="onFlagError(loc.code)"
          />
          <span v-else class="flag-fallback">{{ loc.short }}</span>
        </span>
        <span class="ls-name">{{ loc.label }}</span>
        <span class="ls-code">{{ loc.short }}</span>
        <i v-if="loc.code === current" class="fas fa-check ls-check" aria-hidden="true"></i>
      </button>
    </div>
  </BaseModal>
</template>

<script>
import { LOCALES, setI18nLocale } from '@/i18n';
import BaseModal from '@/components/BaseModal.vue';

export default {
  name: 'LanguageSwitcher',
  components: { BaseModal },
  props: {
    open: { type: Boolean, default: false },
  },
  emits: ['update:open'],
  data() {
    return {
      flagFailed: {},
    };
  },
  computed: {
    locales() { return LOCALES; },
    current() { return this.$i18n.locale; },
  },
  methods: {
    onOpenUpdate(v) { this.$emit('update:open', v); },
    close() { this.$emit('update:open', false); },
    async pick(code) {
      await setI18nLocale(code);
      this.close();
    },
    flagUrl(flag) {
      return `https://flagcdn.com/w40/${flag}.png`;
    },
    onFlagError(code) {
      // Mark this locale's flag as failed so we render the 2-letter pill
      // fallback instead of a broken-image icon.
      this.flagFailed = { ...this.flagFailed, [code]: true };
    },
  },
};
</script>

<style scoped>
/* The dark blurred backdrop + purple-glass card now come from BaseModal.
   This component only styles its own contents. */

.ls-head {
  padding: 16px 56px 4px 18px;
}
.ls-head h3 {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #fff;
}
.ls-sub {
  margin: 4px 0 0;
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.55);
  line-height: 1.45;
}

.ls-hint {
  margin: 8px 18px 6px;
  font-size: 10.5px;
  color: rgba(255, 255, 255, 0.55);
  text-align: left;
}

.ls-body {
  padding: 4px 14px 16px;
  display: flex; flex-direction: column; gap: 6px;
  overflow-y: auto;
}
.ls-row {
  display: flex; align-items: center; gap: 12px;
  width: 100%;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  color: #f4f1ff;
  font-size: 13.5px;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s, border-color 0.15s, transform 0.08s;
}
.ls-row:hover {
  background: rgba(124, 58, 237, 0.18);
  border-color: rgba(192, 38, 211, 0.45);
}
.ls-row:active { transform: scale(0.99); }
.ls-row.is-active {
  background: linear-gradient(135deg, rgba(124, 58, 237, 0.22), rgba(192, 38, 211, 0.18));
  border-color: rgba(192, 38, 211, 0.6);
  box-shadow: 0 0 0 1px rgba(192, 38, 211, 0.3) inset;
}

.ls-flag {
  display: inline-flex; align-items: center; justify-content: center;
  width: 28px; height: 20px;
  border-radius: 4px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.35);
  flex: 0 0 auto;
}
.ls-flag img {
  width: 100%; height: 100%; object-fit: cover; display: block;
}
.flag-fallback {
  display: inline-flex; align-items: center; justify-content: center;
  width: 100%; height: 100%;
  background: linear-gradient(135deg, #7c3aed 0%, #c026d3 100%);
  color: #fff;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.5px;
  line-height: 1;
  text-transform: uppercase;
}
.ls-name {
  flex: 1 1 auto;
  font-weight: 600;
}
.ls-code {
  font-size: 10.5px;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.55);
  font-weight: 700;
}
.ls-check {
  font-size: 11px;
  color: #c026d3;
  margin-left: 4px;
}
</style>
